import { Injectable, inject } from '@angular/core';
import { Firestore, collection, collectionData, doc, docData } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { Venue } from '../models/venue';
import { InternalVenue } from '../models/internalVenue';
import { environment } from '../../environments/environment';

/**
 * Shared read layer for hot Firestore documents and collections.
 *
 * Each distinct key holds ONE live Firestore listener shared by every
 * concurrent subscriber (e.g. the sidebar and the routed venue page);
 * `refCount` tears the listener down when the last subscriber leaves and
 * reopens it fresh on the next subscribe, so idle streams cost nothing.
 *
 * Components must read the venues collection and venue / internal-venue
 * documents through this service instead of opening their own
 * `docData`/`collectionData` listeners.
 */
/**
 * Local snapshot of the venues list so the list paints before Firestore
 * delivers the live collection. Venue documents can run to 100 KB+ each (embedded
 * presets / locationSections), so the full collection is a multi-megabyte
 * download that takes ~1 s even on a warm connection; only the fields the list
 * chrome renders are cached. Scoped to this one listener — nothing else in the
 * app reads from or writes to it.
 */
// Namespaced by Firebase project so a dev-project build and a production build
// served from the same origin (e.g. localhost) never show each other's venues.
const VENUES_CACHE_KEY = `venue-data:${environment.projectId}:venues:v1`;
type CachedVenue = Pick<Venue, 'id' | 'name' | 'imagePath' | 'enabled'>;

function readVenuesCache(): Venue[] | null {
  try {
    const raw = localStorage.getItem(VENUES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Slim rows stand in for full venues until the live snapshot lands; every
    // consumer of getVenues() reads only these fields (list, sidebar, ticket names).
    return parsed as Venue[];
  } catch {
    return null;
  }
}

function writeVenuesCache(venues: Venue[]): void {
  try {
    const slim: CachedVenue[] = venues.map(v => ({
      id: v.id, name: v.name, imagePath: v.imagePath, enabled: v.enabled,
    }));
    localStorage.setItem(VENUES_CACHE_KEY, JSON.stringify(slim));
  } catch {
    // Quota / private mode — the live stream still works without the cache.
  }
}

@Injectable({ providedIn: 'root' })
export class VenueDataService {
  private firestore = inject(Firestore);

  private sharedStreams = new Map<string, Observable<unknown>>();

  private shared<T>(key: string, factory: () => Observable<T>): Observable<T> {
    let stream = this.sharedStreams.get(key) as Observable<T> | undefined;
    if (!stream) {
      stream = factory().pipe(shareReplay({ bufferSize: 1, refCount: true }));
      this.sharedStreams.set(key, stream);
    }
    return stream;
  }

  /**
   * All venues sorted by name — shared by the sidebar dropdown and the venue list.
   * Emits the last cached slim list immediately (when one exists) so the list
   * renders at once, then the live Firestore snapshot replaces it.
   */
  getVenues(): Observable<Venue[]> {
    return this.shared('venues', () => {
      const live$ = (collectionData(collection(this.firestore, 'venues'), { idField: 'id' }) as Observable<Venue[]>).pipe(
        map(venues => [...venues].sort((a, b) =>
          (a.name?.toLowerCase() || '').localeCompare(b.name?.toLowerCase() || ''))),
        tap(venues => writeVenuesCache(venues))
      );
      const cached = readVenuesCache();
      return cached ? live$.pipe(startWith(cached)) : live$;
    });
  }

  /** Live venue document; emits undefined while the document does not exist. */
  getVenue(venueId: string): Observable<Venue | undefined> {
    return this.shared(`venues/${venueId}`, () =>
      docData(doc(this.firestore, 'venues', venueId), { idField: 'id' }) as Observable<Venue | undefined>);
  }

  /** Live internal venue document (feature flags); emits undefined when missing. */
  getInternalVenue(venueId: string): Observable<InternalVenue | undefined> {
    return this.shared(`venues_internal/${venueId}`, () =>
      docData(doc(this.firestore, 'venues_internal', venueId)) as Observable<InternalVenue | undefined>);
  }
}
